/**
 * <s-lazy-video> — loads a muted looping video only as it nears the screen.
 *
 * Wraps a <video data-src="…" preload="none" poster="…">. The poster shows
 * right away; the video file is attached once the tile comes within ~400px
 * of the viewport, plays while at least a quarter of it is visible, and
 * pauses when it scrolls away so off-screen clips stop using data and
 * battery. Visitors who prefer reduced motion keep the still poster.
 */
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

class SLazyVideo extends HTMLElement {
  /** @type {IntersectionObserver | undefined} */
  #loadObserver;
  /** @type {IntersectionObserver | undefined} */
  #playObserver;

  connectedCallback() {
    const video = this.querySelector('video');
    if (!video) return;

    this.#loadObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#attach(video);
        this.#loadObserver?.disconnect();
      },
      { rootMargin: '400px 0px' }
    );
    this.#loadObserver.observe(this.parentElement ?? this);

    this.#playObserver = new IntersectionObserver(
      (entries) => {
        const visible = entries.some((entry) => entry.isIntersecting);
        if (visible && !reduceMotion.matches && video.getAttribute('src')) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      },
      { threshold: 0.25 }
    );
    this.#playObserver.observe(this.parentElement ?? this);
  }

  disconnectedCallback() {
    this.#loadObserver?.disconnect();
    this.#playObserver?.disconnect();
  }

  /** @param {HTMLVideoElement} video */
  #attach(video) {
    const source = video.dataset.src;
    if (!source || video.getAttribute('src')) return;
    if (reduceMotion.matches) return;
    video.src = source;
    video.preload = 'auto';
    video.play().catch(() => {});
  }
}

if (!customElements.get('s-lazy-video')) {
  customElements.define('s-lazy-video', SLazyVideo);
}
